import { useState } from 'react';

import { useDrawdownRepaymentSchedule } from '@api/lendingAPI/lapiHooks';
import { TFormalOffer } from '@helpers/FormalOffers/formalOffers.types';
import { getDurationInMonths } from '@helpers/GetDurationInMonths/getDurationInMonths';
import { FormattedCurrency } from '@iwIntlProvider/FormattedCurrency';
import { tracking } from '@iwoca/frontend-tracking-library';
import { IconAngleRight, IconCalculator, IconTimesDefault } from '@iwoca/icons';
import { Modal, Text } from '@iwoca/orion';
import { OverpayLoanSVG } from '@svgComponents/overpayLoanSVG/OverpayLoanSVG';
import { addMonths, format } from 'date-fns';
import { FormattedMessage } from 'react-intl';

import { RepaymentCalculator } from '../../CompareOffers/ComparisonAndRepaymentModal/RepaymentCalculator/RepaymentCalculator';

const getEarlyRepaymentMonths = (durationInMonths: number): number => {
    if (durationInMonths <= 12) return 2;
    if (durationInMonths <= 15) return 4;
    if (durationInMonths <= 24) return 6;
    return 9;
};

export const RepaymentCalculatorButton = ({
    selectedOffer,
    drawdownAmount
}: {
    selectedOffer: TFormalOffer | undefined;
    drawdownAmount: number | undefined;
}) => {
    const offerDurationMonths = selectedOffer?.duration
        ? getDurationInMonths(selectedOffer.duration)
        : 0;
    const earlyRepaymentMonths = getEarlyRepaymentMonths(offerDurationMonths);
    const earlyFinalRepaymentDate = format(
        addMonths(new Date(), earlyRepaymentMonths),
        'yyyy-MM-dd'
    );
    const [isRepaymentCalculatorOpen, setIsRepaymentCalculatorOpen] =
        useState(false);

    const { data } = useDrawdownRepaymentSchedule({
        offerId: selectedOffer?.offer_id ?? '',
        drawdownAmount: drawdownAmount ?? 0,
        earlyFinalRepaymentDate,
        enabled: Boolean(selectedOffer && drawdownAmount)
    });

    const totalPayable = data?.summary.total_payable ?? null;

    const openCalculator = () => {
        tracking.ampli.earlyRepaymentCalculatorOpened({
            calculator_button: 'loan summary'
        });
        setIsRepaymentCalculatorOpen(true);
    };

    const handleClose = () => {
        tracking.ampli.earlyRepaymentCalculatorClosed();
        setIsRepaymentCalculatorOpen(false);
    };

    return (
        <>
            {selectedOffer && drawdownAmount ? (
                <div
                    className="my-s rounded-s bg-core-95 p-l min-sm:my-xl"
                    data-testid="repayment-calculator-widget"
                >
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-s">
                            <OverpayLoanSVG className="h-[20px] w-[20px] shrink-0" />
                            <Text className="text-xs font-bold text-structure-0 min-sm:text-s">
                                <FormattedMessage
                                    id="_.loan_summary.total_if_repaid_early"
                                    defaultMessage="Total if repaid at {months} months:"
                                    values={{ months: earlyRepaymentMonths }}
                                />
                            </Text>
                        </div>
                        <Text className="text-xs font-bold text-structure-0 min-sm:text-s">
                            {totalPayable !== null ? (
                                <FormattedCurrency value={totalPayable} />
                            ) : (
                                '£--.--'
                            )}
                        </Text>
                    </div>

                    <button
                        className="mt-m flex w-full items-center justify-between rounded-s border-2 border-primary-10 bg-structure-100 px-m py-s hover:bg-secondary-95"
                        onClick={openCalculator}
                    >
                        <div className="flex items-center gap-s">
                            <IconCalculator
                                className="shrink-0 text-primary-10"
                                fontSize="20px"
                            />
                            <Text className="text-xs font-med text-structure-0 min-sm:text-s">
                                <FormattedMessage
                                    id="_.loan_summary.explore_repayment_flexibility"
                                    defaultMessage="Explore our repayment flexibility"
                                />
                            </Text>
                        </div>
                        <IconAngleRight
                            className="shrink-0 text-primary-10"
                            fontSize="20px"
                        />
                    </button>
                </div>
            ) : null}

            {selectedOffer && drawdownAmount && (
                <Modal
                    headerBar
                    isOpen={isRepaymentCalculatorOpen}
                    size="large"
                    onClose={handleClose}
                >
                    {/* Using a custom close button so the z-index can be set to render it above the date input */}
                    <button
                        className="absolute right-l top-xs z-50 flex h-[40px] w-[40px] items-center justify-center rounded-s bg-structure-100 p-0 text-structure-0 hover:bg-structure-95"
                        onClick={handleClose}
                        data-testid="closeIcon"
                    >
                        <IconTimesDefault fontSize="24px" />
                    </button>
                    <RepaymentCalculator
                        selectedOffer={selectedOffer}
                        drawdownAmount={drawdownAmount}
                        defaultDate={earlyFinalRepaymentDate}
                    />
                </Modal>
            )}
        </>
    );
};
